import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";
import {
    GRID_ACTION_TYPES,
    GridActionType,
    QueryKeyTypes
} from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { Dialog } from "../../../assets/vfx/dialog/vfxDialog";
import { VfxDialogProps } from "../../../assets/vfx/dialog/vfxDialogProps";
import { LoadingIndicator } from "../../../assets/vfx/loadingIndicator/vfxLoadingIndicator";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { UserDto } from "../../../services/dto";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { UserForm } from "./userForm";
import { UserPage } from "./userPage";
import { UserService } from "./userService";
import { UserView } from "./userView";
import { ViewProps } from "./props";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

interface UserContainerProps {
    isCommunityDeactive?: boolean | undefined;
}

export const UserContainer: React.FC<UserContainerProps> = (
    props: UserContainerProps
) => {
    const { communityId: paramcommunityId } = useParams();
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const [data, setData] = React.useState<UserDto>();
    const [drawerType, setDrawerType] = React.useState<GridActionType>(GRID_ACTION_TYPES.HIDDEN);
    const [dialogProps, setDialogProps] = React.useState<VfxDialogProps | undefined>(undefined);
    const userQuery = UserService.getUserQuery(data?.id ?? undefined);
    const lookupQuery = UserService.getLookups();
    const { status: saveStatus, mutateAsync: saveUser } = UserService.save();
    const { status: deActivateStatus, mutate: deActivateUser } = UserService.deActivateUser();
    const { status: resetPasswordStatus, mutate: resetPassword } = UserService.resetPassword();
    const { status: unlockStatus, mutate: unlock } = UserService.unlockUser();
    const { status: unlockresendEmail, mutate: resendEmail } = UserService.resendEmail();
    const { status: enableStatus, mutate: enableUser } = UserService.enableUser();
    const { setSearchAction, clearSearchAction } = useActionFromQuery(OnCustomEvent);

    function EditRecord() {
        setData(userQuery.data);
        setDrawerType(GRID_ACTION_TYPES.EDIT);
        setSearchAction(GRID_ACTION_TYPES.EDIT, userQuery.data?.id);
    }

    function OnCustomEvent(
        actionType: string,
        dataId: string,
        userName?: string | undefined
    ) {
        if (actionType === GRID_ACTION_TYPES.UNLOCK) {
            setDialogProps(
                new ViewProps.UserConfirmationDialog().get(
                    () => handleUnLock(dataId),
                    "UnlockAccount",
                    {
                        Name: userName ?? Constants.emptyString,
                    }
                )
            );
        } else if (actionType === GRID_ACTION_TYPES.RESET_PASSWORD) {
            setDialogProps(
                new ViewProps.UserConfirmationDialog().get(
                    () => handleResetPaswword(dataId),
                    "Reset Password",
                    {
                        Name: userName ?? Constants.emptyString,
                    }
                )
            );
        } else if (actionType === GRID_ACTION_TYPES.RESEND_CONFIRMATION_MAIL) {
            setDialogProps(
                new ViewProps.UserConfirmationDialog().get(
                    () => handleResendEmail(dataId),
                    "Resend Email",
                    {
                        Name: userName ?? Constants.emptyString,
                    }
                )
            );
        } else {
            setDialogProps(undefined);
            if (isGridActionType(actionType)) {
                setDrawerType(actionType);
                setData({ id: dataId, communityId: paramcommunityId ?? undefined } as UserDto);
                setSearchAction(actionType, dataId);
            }
        }
    }

    function OnEnableUser(userId: string, userName?: string | undefined) {
        setDialogProps({
            contentText: "Do you wish to enable " + userName + "?",
            contentTitle: "Enable User",
            closeText: "No, Go Back",
            confirmText: "Yes, Continue",
            open: true,
            onClose: () => { setDialogProps(undefined); },
            onConfirm: () => { handleEnableUser(userId); closeDrawer(); },
        }
        );
    }

    const handleSubmit = async (values: UserDto) => {
        const isEdit = data && data.id && data.id !== Constants.emptyGuid;
        values.id = isEdit ? data.id : Constants.emptyGuid;
        return await saveUser(values, { onSuccess: (response: IActionResult<UserDto>) => handleResponse(response, "User Saved successfully") });
    };

    const closeDrawer = () => {
        setDrawerType(GRID_ACTION_TYPES.HIDDEN);
        clearSearchAction();
    }

    const handleDeactivate = () => deActivateUser(data?.id ?? Constants.emptyGuid, { onSuccess: (response: IActionResponse) => handleResponse(response, "User Deactivated successfully") });

    const handleResetPaswword = (dataId: string) =>
        resetPassword({ userId: dataId ?? Constants.emptyGuid }, { onSuccess: (response: IActionResult<{ userId: string | undefined }>) => handleResponse(response, "Reset Password successfully") });

    const handleResendEmail = (dataId: string) =>
        resendEmail({ userId: dataId ?? Constants.emptyGuid, returnUrl: window.location.href }, {
            onSuccess: (response: IActionResult<{ userId: string | undefined, returnUrl: string }>) => handleResponse(response, "Confirmation email send successfully")
        });

    const handleUnLock = (dataId: string) =>
        unlock({ userId: dataId ?? Constants.emptyGuid }, { onSuccess: (response: IActionResult<{ userId: string | undefined }>) => handleResponse(response, "Reset unlocked successfully") });

    const handleEnableUser = (dataId: string) =>
        enableUser({ userId: dataId ?? Constants.emptyGuid }, { onSuccess: (response: IActionResult<{ userId: string | undefined }>) => handleResponse(response, "User enabled successfully") });

    const renderDrawerContent = () => {
        switch (drawerType) {
            case GRID_ACTION_TYPES.NEW:
                return (
                    <UserForm
                        show
                        data={data}
                        onHide={closeDrawer}
                        onSubmit={handleSubmit}
                        lookups={lookupQuery.data}
                        isLoading={lookupQuery.isLoading}
                    />
                );

            case GRID_ACTION_TYPES.EDIT:
                return <UserForm
                    show={!!userQuery.data}
                    data={userQuery.data}
                    lookups={lookupQuery.data}
                    onHide={closeDrawer}
                    onSubmit={handleSubmit}
                    isLoading={userQuery.isLoading}
                />

            case GRID_ACTION_TYPES.VIEW:
            case GRID_ACTION_TYPES.DOUBLE_CLICK:
                return <UserView
                    data={userQuery.data}
                    show={!!userQuery.data}
                    onHide={closeDrawer}
                    onEdit={EditRecord}
                    onDeactivate={handleDeactivate}
                    lookups={lookupQuery.data}
                    isLoading={userQuery.isLoading}
                    onEnableUser={OnEnableUser}
                />

            default:
                return null;
        }
    };

    function handleResponse(response: IActionResponse, snackbarMessage: string) {
        if (response) {
            if (response.isSuccess) {
                showSnackbar(snackbarMessage, MessageSeverity.Success);
                userQuery.refetch();
                queryClient.invalidateQueries({ queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Grid]] });
                closeDrawer();
            } else {
                if (response.errorMessages) {
                    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
                    snackbarMessage = errorMsg ?? "Error";
                }
                showSnackbar(snackbarMessage, MessageSeverity.Error);
            }
        }
        else {
            showSnackbar("Error, unable to process your request please try again later.", MessageSeverity.Error);
        }
    }

    return (
        <>
            {lookupQuery.isLoading ? (
                <LoadingIndicator isFullPage={true} />
            ) : (
                <UserPage
                    onCustomEvent={OnCustomEvent}
                    lookups={lookupQuery.data}
                    isCommunityDeactive={props.isCommunityDeactive}
                ></UserPage>
            )}
            {renderDrawerContent()}
            {dialogProps && (
                <Dialog
                    contentText={dialogProps.contentText}
                    contentTitle={dialogProps.contentTitle}
                    onClose={() => setDialogProps(undefined)}
                    onConfirm={() => {
                        setDialogProps(undefined);
                        dialogProps?.onConfirm();
                    }}
                    open={dialogProps !== undefined}
                    closeText={dialogProps.closeText}
                    confirmText={dialogProps.confirmText}
                />
            )}
        </>
    );
};
